/**
 * Variant + quantity helpers for custom product-info sections.
 * Works with pi-buy-form markup; integrates with Dawn product-form custom element.
 */
(function () {
  if (customElements.get('pi-buy-form')) return;

  customElements.define(
    'pi-buy-form',
    class PiBuyForm extends HTMLElement {
      connectedCallback() {
        this.sectionId = this.dataset.sectionId;
        this.product = JSON.parse(
          this.querySelector('[data-pi-product-json]').textContent
        );
        this.form = this.querySelector('form[data-type="add-to-cart-form"]');
        this.variantInput = this.querySelector('[data-pi-variant-input]');
        this.qtyInput = this.querySelector('[data-pi-qty-input]');
        this.submitBtn = this.querySelector('[data-pi-submit]');
        this.submitText = this.querySelector('[data-pi-submit-text]');

        this.bindVariantPickers();
        this.bindQuantity();
        this.updateVariant();
      }

      get sectionRoot() {
        return document.getElementById(`shopify-section-${this.sectionId}`);
      }

      bindVariantPickers() {
        this.querySelectorAll('[data-pi-option]').forEach((group) => {
          group.addEventListener('click', (event) => {
            const target = event.target.closest('[data-pi-option-value]');
            if (!target || target.disabled || target.classList.contains('disabled')) return;

            group
              .querySelectorAll('[data-pi-option-value]')
              .forEach((el) => el.classList.remove('active', 'is-active'));
            target.classList.add('active', 'is-active');

            if (target.tagName === 'INPUT' && target.type === 'radio') {
              target.checked = true;
            }

            this.updateVariant();
          });
        });

        this.querySelectorAll('[data-pi-option-value][type="radio"]').forEach((input) => {
          input.addEventListener('change', () => this.updateVariant());
        });

        this.querySelectorAll('[data-pi-option-value] + label, label[for^="Option-"]').forEach((label) => {
          label.addEventListener('click', () => {
            setTimeout(() => this.updateVariant(), 0);
          });
        });
      }

      bindQuantity() {
        const min = parseInt(this.qtyInput?.min || '1', 10);
        const max = parseInt(this.qtyInput?.max || '99', 10);

        this.querySelector('[data-pi-qty-minus]')?.addEventListener('click', () => {
          const next = Math.max(min, (parseInt(this.qtyInput.value, 10) || 1) - 1);
          this.qtyInput.value = next;
          this.syncQtyDisplay(next);
        });

        this.querySelector('[data-pi-qty-plus]')?.addEventListener('click', () => {
          const next = Math.min(max, (parseInt(this.qtyInput.value, 10) || 1) + 1);
          this.qtyInput.value = next;
          this.syncQtyDisplay(next);
        });
      }

      syncQtyDisplay(value) {
        const display = this.querySelector('[data-pi-qty-display]');
        if (display) display.textContent = value;
      }

      getSelectedOptions() {
        const selected = [];
        this.querySelectorAll('[data-pi-option]').forEach((group) => {
          const position = parseInt(group.dataset.optionPosition, 10);
          const checked = group.querySelector('[data-pi-option-value]:checked');
          const active = group.querySelector(
            '[data-pi-option-value].active, [data-pi-option-value].is-active'
          );
          const el = checked || active;
          if (el) selected[position - 1] = el.dataset.value || el.value;
        });
        return selected;
      }

      findVariant() {
        const selected = this.getSelectedOptions();
        if (!selected.length) {
          return this.product.variants.find((v) => v.id === this.product.selected_or_first_available_variant?.id)
            || this.product.variants[0];
        }
        return this.product.variants.find((variant) =>
          variant.options.every((opt, index) => opt === selected[index])
        );
      }

      updateVariant() {
        const variant = this.findVariant();
        if (!variant) return;

        this.variantInput.value = variant.id;
        const available = variant.available;
        this.variantInput.disabled = !available;

        if (this.submitBtn) {
          this.submitBtn.disabled = !available;
        }
        if (this.submitText) {
          this.submitText.textContent = available
            ? this.submitText.dataset.addText || 'Add to cart'
            : this.submitText.dataset.soldOutText || 'Sold out';
        }

        this.updatePriceDisplay(variant);
        this.markUnavailableOptions();

        this.sectionRoot?.dispatchEvent(
          new CustomEvent('pi:variant-change', {
            detail: { variant },
            bubbles: true,
          })
        );
      }

      updatePriceDisplay(variant) {
        const root = this.sectionRoot;
        if (!root) return;

        const priceEl = root.querySelector('[data-pi-price]');
        const compareEl = root.querySelector('[data-pi-compare-price]');
        const badgeEl = root.querySelector('[data-pi-sale-badge]');

        if (priceEl && variant.price != null) {
          priceEl.textContent = this.formatMoney(variant.price);
        }

        if (compareEl) {
          if (variant.compare_at_price && variant.compare_at_price > variant.price) {
            compareEl.textContent = this.formatMoney(variant.compare_at_price);
            compareEl.hidden = false;
          } else {
            compareEl.hidden = true;
          }
        }

        if (badgeEl) {
          badgeEl.hidden = !(variant.compare_at_price && variant.compare_at_price > variant.price);
        }
      }

      markUnavailableOptions() {
        const selected = this.getSelectedOptions();
        this.querySelectorAll('[data-pi-option]').forEach((group) => {
          const position = parseInt(group.dataset.optionPosition, 10);
          group.querySelectorAll('[data-pi-option-value]').forEach((el) => {
            const value = el.dataset.value || el.value;
            const hypothetic = [...selected];
            hypothetic[position - 1] = value;
            const match = this.product.variants.some(
              (v) =>
                v.available &&
                v.options.every((opt, idx) => !hypothetic[idx] || opt === hypothetic[idx])
            );
            el.classList.toggle('disabled', !match);
            if (el.tagName === 'INPUT') el.disabled = !match;
          });
        });
      }

      formatMoney(cents) {
        if (window.Shopify?.formatMoney) {
          return window.Shopify.formatMoney(cents, window.theme?.moneyFormat);
        }
        const amount = (cents / 100).toFixed(2);
        return `$${amount}`;
      }
    }
  );

})();
